import { HttpTypes } from '@/types';
import { IFaq } from '@/types/faq';
import { ITermsCondition } from '@/types/general';
import { BaseBlogPost } from '@/types/http/blog/blog-posts/common';
import { IProduct, IProductDetailPriceBook, IProductReview } from '@/types/product';
import { IBuilder } from '@/types/site';
import { IMetadata } from '@/types/storepreferences';
import { AxiosInstance, AxiosRequestConfig } from 'axios';

class Homepage {
  // this client will use publicAPI instance to make request
  private client: AxiosInstance;
  private sitePath = '/sites';
  private productPath = '/product/store';
  public host = '';

  constructor(client: AxiosInstance) {
    client.interceptors.request.use();

    this.client = client;
  }

  interceptRequest(config: AxiosRequestConfig) {
    config.headers = {
      ...config.headers,
    };

    return config;
  }

  async retrievePage(
    pageName: string,
    themeId?: string,
    token?: string
  ): Promise<HttpTypes.IResponse<IBuilder>> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.get(`${this.sitePath}/ui`, {
      params: {
        pageName,
        theme_id: themeId,
        token,
      },
      headers: {
        Origin: this.host,
        'X-Jstore-Host': this.host,
      },
    });
    return result.data;
  }

  async retriveMenu(id: string): Promise<HttpTypes.StoreMenuResponse> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.get(`${this.sitePath}/menu/${id}`, {
      headers: {
        Origin: this.host,
        'X-Jstore-Host': this.host,
      },
    });
    return result.data;
  }

  async retrieveMetadata(pageName: string, slug?: string): Promise<HttpTypes.IResponse<IMetadata>> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.get(`${this.sitePath}/metadata`, {
      params: {
        pageName,
        slug,
      },
      headers: {
        Origin: this.host,
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }

  async retrieveProducts(
    queryParams: HttpTypes.FindParams,
    token?: string,
    locationId?: string
  ): Promise<HttpTypes.StoreProductListResponse> {
    this.interceptRequest({ ...this.client.request });

    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'X-Location-Id': locationId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.get(`/product/store/collections/${queryParams.slug}`, {
      params: queryParams,
      headers,
    });
    return result.data;
  }

  async retrieveAllProducts(
    queryParams: HttpTypes.FindParams,
    token?: string,
    locationId?: string
  ): Promise<HttpTypes.StoreProductListResponse> {
    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'X-Location-Id': locationId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.get('/product/store/product/list', {
      params: queryParams,
      headers,
    });
    return result.data;
  }

  async retrieveProductDetail(
    slug: string,
    token?: string,
    locationId?: string
  ): Promise<HttpTypes.IResponse<IProduct>> {
    this.interceptRequest({ ...this.client.request });

    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'X-Location-Id': locationId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.get(`/product/store/product/${slug}`, {
      headers,
    });
    return result.data;
  }

  async retrieveBlogspotDetail(slug: string): Promise<HttpTypes.IResponse<BaseBlogPost>> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.get(`${this.sitePath}/blogspot/public/${slug}`, {
      headers: {
        Origin: this.host,
        'X-Jstore-Host': this.host,
      },
    });
    return result.data;
  }

  async retrieveQtyProductVariant(
    productGroupId: string,
    payload: HttpTypes.StoreProductVariantStockPayload,
    locationId?: string
  ): Promise<
    HttpTypes.IResponse<{
      stock: string;
    }>
  > {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.post(
      `/product/store/stocks/variant/${productGroupId}`,
      payload,
      {
        headers: {
          Origin: this.host,
          'X-Jstore-Host': this.host,
          'X-Location-Id': locationId,
        },
      }
    );
    return result.data;
  }

  async retrieveSearchProduct(
    queryParams: HttpTypes.FindParams,
    token?: string
  ): Promise<HttpTypes.StoreProductListResponse> {
    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.get(`product/store/product/list`, {
      params: queryParams,
      headers,
    });
    return result.data;
  }

  async passwordProtect(password: string) {
    const result = await this.client.post(
      `${this.sitePath}/security/access`,
      { password },
      {
        headers: {
          Origin: this.host,
          'X-Jstore-Host': this.host,
        },
      }
    );
    return result.data;
  }

  async categoryList(query: HttpTypes.FindParams): Promise<HttpTypes.StoreProductCategoryResponse> {
    const result = await this.client.get(`${this.productPath}/category/list`, {
      params: query,
      headers: {
        Origin: this.host,
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }

  async getFaq(): Promise<HttpTypes.IResponse<IFaq[]>> {
    const result = await this.client.get(`${this.sitePath}/public/faq`, {
      headers: {
        Origin: this.host,
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }

  async getTermsCondition(): Promise<HttpTypes.IResponse<ITermsCondition>> {
    const result = await this.client.get(`${this.sitePath}/public/terms-condition`, {
      headers: {
        Origin: this.host,
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }

  async retrieveLocationMultiOrigin(): Promise<HttpTypes.MultiOriginLocationResponse> {
    {
      this.interceptRequest({ ...this.client.request });
      const result = await this.client.get(`${this.sitePath}/locations`, {
        headers: {
          Origin: this.host,
          'X-Jstore-Host': this.host,
        },
      });
      return result.data;
    }
  }

  async retrieveRelatedProduct(params: HttpTypes.FindParams, token?: string, locationId?: string) {
    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'X-Location-Id': locationId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.get(
      `${this.productPath}/product/related/${params.slug as string}`,
      {
        params,
        headers,
      }
    );
    return result.data;
  }

  async retrieveProductDetailPriceBook(
    slug: string,
    token?: string,
    locationId?: string
  ): Promise<HttpTypes.IResponse<IProductDetailPriceBook[]>> {
    this.interceptRequest({ ...this.client.request });

    const headers = {
      Origin: this.host,
      'X-Jstore-Host': this.host,
      'X-Location-Id': locationId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const result = await this.client.get(`/product/store/product/pricebook/${slug}`, {
      headers,
    });

    return result.data;
  }

  async listProductReview(
    productId: string,
    queryParams?: HttpTypes.FindParams
  ): Promise<HttpTypes.IResponse<IProductReview[]>> {
    this.interceptRequest({ ...this.client.request });

    const result = await this.client.get(`/product/store/product/reviews/${productId}`, {
      params: queryParams,
      headers: {
        Origin: this.host,
        'X-Jstore-Host': this.host,
      },
    });

    return result.data;
  }

  async retrieveThemeStyle(
    themeId?: string,
    token?: string
  ): Promise<HttpTypes.IResponse<Record<string, any>>> {
    this.interceptRequest({ ...this.client.request });
    const result = await this.client.get(`${this.sitePath}/theme/style`, {
      params: {
        theme_id: themeId,
        token,
      },
      headers: {
        Origin: this.host,
        'X-Jstore-Host': this.host,
      },
      fetchOptions: {
        cache: 'force-cache',
        next: {
          tags: ['theme-style'],
        },
      },
    });
    return result.data;
  }
}

export default Homepage;
